/**
 * The video trainings, as content rather than as links off the site.
 *
 * Three ecosystem steps pointed at carenglasser.com/trainings/... . That was
 * the right answer when the trainings only existed there, but it means the
 * step that says "I'm ready to implement, show me how it actually works" —
 * the most committed thing anyone clicks on a tool path — hands the visitor to
 * a different domain, a different brand, and a page whose own navigation
 * points back at aitools.carenglasser.com, the old address. Their progress
 * does not follow them and neither does their stack.
 *
 * The videos are on Open.Video either way; only the page around them changes.
 * carenglasser.com keeps its copies, which are indexed and linked from her
 * blog. These are the Smart Stack copies, sitting inside the path they belong
 * to, with the next step in that path at the bottom.
 *
 * Copy is Caren's, taken from the trainings themselves.
 */

export interface Training {
  slug: string;
  /** Open.Video id. Same video as on carenglasser.com. */
  videoId: string;
  title: string;
  minutes: number;
  /** The tool path this belongs to, for the header context and the back link. */
  toolSlug: string;
  toolLabel: string;
  description: string;
  intro: string;
  learn: string[];
  forYou: string;
  /** Where to send someone who finishes it. */
  next: { label: string; href: string };
}

export const TRAININGS: Training[] = [
  {
    slug: "stop-generating-start-refining",
    videoId: "UYnkMJnkZIu",
    title: "Stop Generating, Start Refining",
    minutes: 12,
    toolSlug: "claude",
    toolLabel: "Claude",
    description:
      "Learn how to use Claude to refine your writing, prompt smarter, and work with AI without losing your voice.",
    intro:
      "Caren walks through her Smart Stack workflow for using Claude as a thinking and refinement partner, not just another content generator.",
    learn: [
      "Why clarity comes before tools, and how that mindset shift changes everything",
      "How to use Claude to refine your writing without losing your unique voice",
      "5 Claude Quick Wins you can use in your business today",
      "How to prompt Claude for better results with less effort",
      "When to reach for Claude, and when to use a different tool",
    ],
    forYou:
      "You want to work smarter with AI, not harder. You don't need a tech background. You just need to show up with something real, and Claude will help you make it better.",
    next: { label: "Claude Quick Wins guide", href: "/resources/claude/quick-wins-full" },
  },
  {
    slug: "stop-renting-your-audience",
    videoId: "RleoRtmYAB2",
    title: "Stop Renting Your Audience",
    minutes: 15,
    toolSlug: "open-video",
    toolLabel: "Open.Video",
    description:
      "Build a creator-owned video channel, own your subscribers' emails, and monetize your videos from day one.",
    intro:
      "Platform ownership is the piece most video workflows are missing. This walks through moving your channel onto ground you control.",
    learn: [
      "Why platform ownership is the missing piece in your Smart Stack workflow",
      "How to import your YouTube videos into Open.Video in minutes",
      "How to fully brand your channel: your logo, your colours, your domain",
      "How to embed your channel so search engines index your pages, not YouTube's",
      "How to own your subscribers' emails and reach your audience directly",
      "How to monetize your videos from day one, with no eligibility requirements",
      "How to show up on both YouTube and your own domain for maximum visibility",
    ],
    forYou:
      "You have been building an audience on someone else's platform and want the list, the branding and the revenue to be yours.",
    next: { label: "Open.Video Hook Library", href: "/resources/open-video/hook-library" },
  },
  {
    slug: "automate-your-social-media",
    videoId: "RcOwRtK6B6u",
    title: "Automate Your Social Media",
    minutes: 15,
    toolSlug: "opus-clip",
    toolLabel: "Opus Clip",
    description:
      "Turn one long-form video into dozens of platform-ready short clips automatically.",
    intro:
      "One recording, dozens of clips, no editing skills. This is the repeatable workflow for showing up consistently without making new content every day.",
    learn: [
      "How Opus Clip fits into your Smart Stack",
      "Turn one video into dozens of clips automatically",
      "No editing skills required",
      "Platform-ready clips for Instagram, TikTok and LinkedIn",
      "Your repeatable workflow for consistent content",
    ],
    forYou:
      "You are already making long-form video and want it to work harder without adding hours to your week.",
    next: { label: "All Opus Clip steps", href: "/resources/opus-clip" },
  },
];

export function trainingBySlug(slug: string): Training | undefined {
  return TRAININGS.find(t => t.slug === slug);
}

/** The on-site path for a training, used by the route and by ecosystemData. */
export function trainingPath(slug: string): string {
  return `/trainings/${slug}`;
}
